import type { IdentityProviderSaveFailedAction } from '@Application/Security/Sso/Actions/identityProviderSaveFailed';
import SaveIdentityProvider from '@Application/Security/Sso/Commands/SaveIdentityProvider';
import FetchPasswordSignInUserCount from '@Application/Security/Sso/Queries/FetchPasswordSignInUserCount';
import FetchSsoModes from '@Application/Security/Sso/Queries/FetchSsoModes';
import fetchPasswordSignInUserCount from '@Application/Security/Sso/fetchPasswordSignInUserCount';
import fetchSsoModes from '@Application/Security/Sso/fetchSsoModes';
import saveIdentityProvider from '@Application/Security/Sso/saveIdentityProvider';
import { theme } from '@Guidelines/Theme/theme';
import ConnectionTestResult from '@UI/Pages/Administration/Sso/SsoFormContainer/ConnectionTestResult';
import DeleteConfigurationButton from '@UI/Pages/Administration/Sso/SsoFormContainer/Actions/DeleteConfigurationButton';
import ExclusiveModeConfirmDialog from '@UI/Pages/Administration/Sso/SsoFormContainer/ConfirmationDialogs/ExclusiveModeConfirmDialog';
import IdentityProviderForm from '@UI/Pages/Administration/Sso/SsoFormContainer/IdentityProviderForm';
import TestConnectionButton from '@UI/Pages/Administration/Sso/SsoFormContainer/Actions/TestConnectionButton';
import IdentityProviderFormData from '@UI/Pages/Administration/Sso/SsoFormContainer/FormData/IdentityProviderFormData';
import isSwitchToExclusiveMode from '@UI/Pages/Administration/Sso/SsoFormContainer/Helpers/isSwitchToExclusiveMode';
import type IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 1.5rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const actionsRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.75rem;
`;

type Props = {
	readonly identityProvider: IdentityProviderView | null;
	readonly accountId: number;
};

/**
 * Hosts the identity provider form and owns its whole registration flow: field state, validation,
 * the exclusive-mode confirmation and the password sign-in count that informs it, and the save
 * submission itself. `identityProvider` and `accountId` are already resolved by the parent;
 * re-fetching either here would duplicate the request.
 */
const SsoFormContainer: React.FC<Props> = ({ identityProvider, accountId }) => {
	const dispatch = useDispatch();

	const [formData, setFormData] = React.useState(() => IdentityProviderFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isExclusiveConfirmVisible, setIsExclusiveConfirmVisible] = React.useState(false);

	const formId = React.useId();

	const { data: passwordSignInUserCount } = useStoreQuery({
		selector: (state) => state.ssoStore.passwordSignInUserCount,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => dispatch(fetchPasswordSignInUserCount(new FetchPasswordSignInUserCount(accountId))),
		deps: [accountId],
	});

	const { data: ssoModes } = useStoreQuery({
		selector: (state) => state.ssoModesStore.ssoModes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSsoModes(new FetchSsoModes())),
	});

	// Reflects the loaded (or just-saved) configuration into the form. Runs only when the store
	// reference changes — never on every render — so it clears the never-reprefilled `clientSecret`
	// after a successful save without wiping the administrator's in-progress typing while creating.
	React.useEffect(() => {
		if (identityProvider !== null) {
			setFormData(IdentityProviderFormData.fromView(identityProvider));
			return;
		}

		setFormData((current) => (current.isCreation ? current : IdentityProviderFormData.createEmpty()));
	}, [identityProvider]);

	const handleModeChange = React.useCallback((value: string) => {
		setFormData((current) => current.setMode(value));
	}, []);

	const handleIsEnabledChange = React.useCallback((value: boolean) => {
		setFormData((current) => current.setIsEnabled(value));
	}, []);

	const handleIssuerUrlChange = React.useCallback((value: string) => {
		setFormData((current) => current.setIssuerUrl(value));
	}, []);

	const handleClientIdChange = React.useCallback((value: string) => {
		setFormData((current) => current.setClientId(value));
	}, []);

	const handleClientSecretChange = React.useCallback((value: string) => {
		setFormData((current) => current.setClientSecret(value));
	}, []);

	const handleEmailDomainsChange = React.useCallback((domains: readonly string[]) => {
		setFormData((current) => current.setEmailDomains(domains));
	}, []);

	const performSave = React.useCallback(() => {
		setIsSubmitting(true);

		dispatch(
			saveIdentityProvider(
				new SaveIdentityProvider(
					accountId,
					formData.mode,
					formData.isEnabled,
					formData.issuerUrl,
					formData.clientId,
					formData.clientSecret,
					formData.emailDomains,
				),
			),
		)
			.catch((action: IdentityProviderSaveFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'IDENTITY_PROVIDER_SAVE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [accountId, formData, dispatch]);

	const handleFormSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		if (isSwitchToExclusiveMode(formData.mode, identityProvider?.mode.value ?? null)) {
			setIsExclusiveConfirmVisible(true);
			return;
		}

		performSave();
	}, [formData, identityProvider, performSave]);

	const handleExclusiveConfirmHide = React.useCallback(() => {
		setIsExclusiveConfirmVisible(false);
	}, []);

	const handleExclusiveConfirmAccept = React.useCallback(() => {
		setIsExclusiveConfirmVisible(false);
		performSave();
	}, [performSave]);

	const passwordSignInUserCountValue = passwordSignInUserCount?.passwordSignInUserCount ?? 0;

	return (
		<React.Fragment>
			<div css={cardStyle}>
				<IdentityProviderForm
					formId={formId}
					onSubmit={handleFormSubmit}
					formData={formData}
					identityProvider={identityProvider}
					ssoModes={ssoModes}
					onModeChange={handleModeChange}
					onIssuerUrlChange={handleIssuerUrlChange}
					onClientIdChange={handleClientIdChange}
					onClientSecretChange={handleClientSecretChange}
					onEmailDomainsChange={handleEmailDomainsChange}
					onIsEnabledChange={handleIsEnabledChange}
				/>

				<ConnectionTestResult identityProvider={identityProvider} />

				<div css={actionsRowStyle}>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
					{identityProvider !== null ? <TestConnectionButton accountId={accountId} /> : null}
					{identityProvider !== null ? <DeleteConfigurationButton accountId={accountId} /> : null}
				</div>
			</div>

			<ExclusiveModeConfirmDialog
				visible={isExclusiveConfirmVisible}
				onHide={handleExclusiveConfirmHide}
				onAccept={handleExclusiveConfirmAccept}
				hasConfiguration={identityProvider !== null}
				passwordSignInUserCount={passwordSignInUserCountValue}
			/>
		</React.Fragment>
	);
};

export default SsoFormContainer;
